
import { supabase } from "@/integrations/supabase/client";
import { MonitoringEvent } from "@/types/monitoring";

/**
 * Calculate an integrity score based on monitoring events
 * @param events List of monitoring events for a test response
 * @returns Integrity score (0-100)
 */
export const calculateIntegrityScore = (events: MonitoringEvent[]): number => {
  if (!events || events.length === 0) return 100;
  
  // Initialize with perfect score
  let score = 100;
  let totalDeduction = 0;
  
  // Track which event types we've already counted to avoid double-counting
  const countedEventTypes: Record<string, boolean> = {};
  
  // Process each event and apply deductions
  events.forEach(event => {
    // Apply deductions based on event severity
    const deduction = getDeductionForEvent(event);
    
    // For tab switching and window blur, count only once per type
    if (event.event_type === 'tab_switch' || event.event_type === 'window_blur') {
      if (!countedEventTypes[event.event_type]) {
        totalDeduction += deduction;
        countedEventTypes[event.event_type] = true;
      }
    } else {
      // For other event types, count each occurrence
      totalDeduction += deduction;
    }
  });
  
  // Apply total deduction, ensuring score doesn't go below 0
  score = Math.max(0, score - totalDeduction);
  
  return Math.round(score);
};

/**
 * Determine the score deduction for a monitoring event based on type and severity
 * @param event The monitoring event
 * @returns Score deduction amount
 */
const getDeductionForEvent = (event: MonitoringEvent): number => {
  const { event_type, severity } = event;
  
  // Base deduction on event type and multiply by severity
  const baseDeduction = 
    event_type === 'tab_switch' ? 10 :
    event_type === 'window_blur' ? 10 :
    event_type === 'timing_anomaly' ? 15 :
    event_type === 'ai_content' ? 25 :
    event_type === 'content_similarity' ? 20 :
    event_type === 'device_location' ? 15 :
    event_type === 'clipboard_action' ? 15 : 0;
    
  return baseDeduction * severity;
};

/**
 * Update the integrity score for a test response
 * @param responseId ID of the test response
 * @param events List of monitoring events
 */
export const updateIntegrityScore = async (responseId: string, events: MonitoringEvent[]): Promise<void> => {
  try {
    // Calculate the integrity score
    const score = calculateIntegrityScore(events);
    
    // Update the test response with the calculated score
    const { error } = await supabase
      .from('test_responses')
      .update({ integrity_score: score })
      .eq('id', responseId);
    
    if (error) {
      console.error('Error updating integrity score:', error);
    }
  } catch (error) {
    console.error('Error in updateIntegrityScore:', error);
  }
};
